import { useState } from 'react';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useFacilities, Facility } from '../../contexts/FacilitiesContext';
import { facilities as mockFacilities } from '../../data/mockData';
import { supabase } from '../../lib/supabase';

const iconOptions = [
  { value: 'ArrowUpDown', label: 'ArrowUpDown (مصعد)' },
  { value: 'CarFront', label: 'CarFront (جراج)' },
  { value: 'Shield', label: 'Shield (أمن)' },
  { value: 'Waves', label: 'Waves (مسبح)' },
  { value: 'Trees', label: 'Trees (حديقة)' },
  { value: 'Wind', label: 'Wind (مكيف)' },
  { value: 'Flame', label: 'Flame (تدفئة)' },
  { value: 'Wifi', label: 'Wifi (إنترنت)' },
];

export default function AdminFacilities() {
  const { facilities, loading, addFacility, updateFacility, deleteFacility, refreshFacilities } = useFacilities();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [addForm, setAddForm] = useState({
    name: '',
    nameEn: '',
    icon: 'ArrowUpDown',
  });

  const [editForm, setEditForm] = useState({
    name: '',
    nameEn: '',
    icon: 'ArrowUpDown',
  });

  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا المرفق؟')) return;
    
    try {
      setSaving(true);
      await deleteFacility(id);
    } catch (err) {
      console.error('Error deleting facility:', err);
      alert('حدث خطأ أثناء حذف المرفق');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (facility: Facility) => {
    setEditingFacility(facility);
    setEditForm({
      name: facility.name,
      nameEn: facility.nameEn,
      icon: facility.icon,
    });
    setIsEditDialogOpen(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!addForm.name.trim() || !addForm.nameEn.trim()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      await addFacility({
        name: addForm.name.trim(),
        nameEn: addForm.nameEn.trim(),
        icon: addForm.icon,
      });
      setAddForm({ name: '', nameEn: '', icon: 'ArrowUpDown' });
      setIsAddDialogOpen(false);
    } catch (err) {
      console.error('Error adding facility:', err);
      alert('حدث خطأ أثناء إضافة المرفق');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingFacility) return;
    
    if (!editForm.name.trim() || !editForm.nameEn.trim()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setSaving(true);
      await updateFacility(editingFacility.id, {
        name: editForm.name.trim(),
        nameEn: editForm.nameEn.trim(),
        icon: editForm.icon,
      });
      setEditingFacility(null);
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating facility:', err);
      alert('حدث خطأ أثناء تحديث المرفق');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFacilities = async () => {
    if (!confirm('سيتم مزامنة المرافق الافتراضية إلى قاعدة البيانات. هل تريد المتابعة؟')) return;
    
    try {
      setSyncing(true);
      
      const facilitiesToInsert = mockFacilities.map(f => ({
        id: f.id,
        name: f.name,
        name_en: f.nameEn,
        icon: f.icon,
      }));
      
      const { error } = await supabase
        .from('facilities')
        .upsert(facilitiesToInsert, { onConflict: 'id' });
      
      if (error) throw error;
      
      alert('تم مزامنة المرافق بنجاح!');
      refreshFacilities();
    } catch (err) {
      console.error('Error syncing facilities:', err);
      alert('حدث خطأ أثناء مزامنة المرافق');
    } finally {
      setSyncing(false);
    }
  };

  const renderIcon = (iconName: string) => {
    const iconMap: Record<string, string> = {
      'ArrowUpDown': '↕️',
      'CarFront': '🚗',
      'Shield': '🛡️',
      'Waves': '🌊',
      'Trees': '🌳',
      'Wind': '💨',
      'Flame': '🔥',
      'Wifi': '📶',
    };
    return iconMap[iconName] || '⚡';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">إدارة المرافق</h1>
          <p className="text-gray-600">إضافة وتعديل وحذف مرافق العقارات</p>
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSyncFacilities}
            disabled={syncing}
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'مزامنة المرافق'}
          </Button>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white">
                <Plus className="w-5 h-5 ml-2" />
                إضافة مرفق جديد
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>إضافة مرفق جديد</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddSubmit} className="space-y-4">
                {/* Quick Add Predefined Facilities */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-gray-700">إضافة سريعة:</label>
                  <div className="flex flex-wrap gap-2">
                    {mockFacilities
                      .filter(mf => !facilities.some(f => f.nameEn === mf.nameEn))
                      .map(facility => (
                        <button
                          key={facility.id}
                          type="button"
                          onClick={async () => {
                            try {
                              await addFacility({
                                name: facility.name,
                                nameEn: facility.nameEn,
                                icon: facility.icon,
                              });
                            } catch (err) {
                              console.error('Error adding facility:', err);
                            }
                          }}
                          disabled={saving}
                          className="px-3 py-1.5 bg-white border border-[#D4AF37] text-[#8B7109] rounded-full text-sm hover:bg-[#D4AF37] hover:text-white transition-colors disabled:opacity-50"
                        >
                          + {facility.name}
                        </button>
                      ))}
                    {mockFacilities.every(mf => facilities.some(f => f.nameEn === mf.nameEn)) && (
                      <span className="text-sm text-gray-500">جميع المرافق المحددة مضافة بالفعل</span>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">اسم المرفق (عربي) *</label>
                  <Input 
                    placeholder="مثال: مصعد"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">اسم المرفق (إنجليزي) *</label>
                  <Input 
                    placeholder="Example: elevator"
                    value={addForm.nameEn}
                    onChange={(e) => setAddForm({ ...addForm, nameEn: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">الأيقونة</label>
                  <select 
                    className="w-full px-3 py-2 border rounded-lg"
                    value={addForm.icon}
                    onChange={(e) => setAddForm({ ...addForm, icon: e.target.value })}
                  >
                    {iconOptions.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#D4AF37] hover:bg-[#B8960C] text-white"
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ المرفق'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setAddForm({ name: '', nameEn: '', icon: 'ArrowUpDown' });
                      setIsAddDialogOpen(false);
                    }}
                    disabled={saving}
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Facilities Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {facilities.map((facility) => (
          <div key={facility.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-14 h-14 bg-[#FFF9E6] rounded-full flex items-center justify-center mb-3">
                <span className="text-[#D4AF37] text-2xl">{renderIcon(facility.icon)}</span>
              </div>
              <h3 className="font-bold text-gray-800">{facility.name}</h3>
              <p className="text-sm text-gray-600">{facility.nameEn}</p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => handleEdit(facility)}
              >
                <Edit className="w-4 h-4 ml-2" />
                تعديل
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(facility.id)}
                disabled={saving}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تعديل المرفق</DialogTitle>
          </DialogHeader>
          {editingFacility && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">اسم المرفق (عربي) *</label>
                <Input 
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">اسم المرفق (إنجليزي) *</label>
                <Input 
                  value={editForm.nameEn}
                  onChange={(e) => setEditForm({ ...editForm, nameEn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الأيقونة</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={editForm.icon}
                  onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                >
                  {iconOptions.map(icon => (
                    <option key={icon.value} value={icon.value}>{icon.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B8960C] text-white"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ التعديلات'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setEditingFacility(null);
                    setIsEditDialogOpen(false);
                  }}
                  disabled={saving}
                >
                  إلغاء
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}